import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import Avatar from 'srcReact/components/common2/Avatar'
import styles from './styles.module.scss'
import LightProfileModal from 'srcReact/components/common2/LightProfileModal'
import Icon from 'srcReact/components/icons/Icon'
import { getDescryptedMedia } from 'srcReact/helpers/utils'
import { useTranslation } from 'react-i18next'

function MemberOverview({
  participants,
  organizers,
  selectParticipantsTab,
  className,
  totalUserCount,
  maxOrganizersMobile = appConstantSettings.MAX_ORGANIZERS_MOBILE,
  maxOrganizersTablet = appConstantSettings.MAX_ORGANIZERS_TABLET,
  maxOrganizersDesktop = appConstantSettings.MAX_ORGANIZERS_DESKTOP,
  organizerCSS,
  show = true,
  //for three dot options in light profile
  handleAddCoorganizer,
  handleRemoveCoorganizer,
  handleRemoveFromGroup,
  type,
  isMixrOver = false,
  isStaffMember
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const {
    login: { user: loggedInUser },
    groupHome: {
      memberRole: {
        userId: groupRoleChangedForUserId,
        role: groupRole,
        type: groupRoleUpdateType,
        success: groupSuccess
      }
    },
    mixr: {
      memberRole: { userId: mixrRoleChangedForUserId, role: mixrRole, type: mixrRoleUpdateType, success: mixrSuccess }
    },
    common: { isNative, isMobile, isTablet }
  } = useSelector((state) => state)
  const [members, setMembers] = useState([])
  const [staff, setStaff] = useState([])
  const [remainingCount, setRemainingCount] = useState(0)
  const [membersWithDecryptedPic, setMembersWithDecryptedPic] = useState([])
  const [staffWithDecryptedPic, setStaffWithDecryptedPic] = useState([])
  const [modalData, setModalData] = useState({
    isShowUserModal: false,
    selectedModalUser: {}
  })

  useEffect(() => {
    //for group page  -- reorganize member view based on changed role
    if (groupSuccess && groupRoleChangedForUserId) {
      if (groupRoleUpdateType === 'add') {
        const user = membersWithDecryptedPic.find((x) => x.id == groupRoleChangedForUserId)
        if (user) {
          user.role = groupRole
          const updatedNormalMembers = membersWithDecryptedPic.filter((x) => x.id != groupRoleChangedForUserId)
          setMembersWithDecryptedPic(updatedNormalMembers)
          setStaffWithDecryptedPic([...staffWithDecryptedPic, user])
        }
      } else if (groupRoleUpdateType === 'remove') {
        const user = staffWithDecryptedPic.find((x) => x.id == groupRoleChangedForUserId)
        if (user) {
          user.role = groupRole
          const updatedStaffMembers = staffWithDecryptedPic.filter((x) => x.id != groupRoleChangedForUserId)
          setStaffWithDecryptedPic(updatedStaffMembers)
          setMembersWithDecryptedPic([...membersWithDecryptedPic, user])
        }
      }
    }
  }, [groupSuccess])

  useEffect(() => {
    //for mixr page -- reorganize member view based on changed role
    if (mixrSuccess && mixrRoleChangedForUserId) {
      if (mixrRoleUpdateType === 'add') {
        const user = membersWithDecryptedPic.find((x) => x.id == mixrRoleChangedForUserId)
        if (user) {
          user.role = mixrRole
          const updatedNormalMembers = membersWithDecryptedPic.filter((x) => x.id != mixrRoleChangedForUserId)
          setMembersWithDecryptedPic(updatedNormalMembers)
          setStaffWithDecryptedPic([...staffWithDecryptedPic, user])
        }
      } else if (mixrRoleUpdateType === 'remove') {
        const user = staffWithDecryptedPic.find((x) => x.id == mixrRoleChangedForUserId)
        if (user) {
          user.role = mixrRole
          const updatedStaffMembers = staffWithDecryptedPic.filter((x) => x.id != mixrRoleChangedForUserId)
          setStaffWithDecryptedPic(updatedStaffMembers)
          setMembersWithDecryptedPic([...membersWithDecryptedPic, user])
        }
      }
    }
  }, [mixrSuccess])

  useEffect(() => {
    if (organizers) {
      //calculation of organizers
      const maxOrganizers = isMobile ? maxOrganizersMobile : isTablet ? maxOrganizersTablet : maxOrganizersDesktop
      const total = isMobile
        ? appConstantSettings.MAX_TOTAL_MOBILE
        : isTablet
        ? appConstantSettings.MAX_TOTAL_TABLET
        : appConstantSettings.MAX_TOTAL_DESKTOP

      if (totalUserCount > total) {
        setRemainingCount(totalUserCount - total)
      } else {
        setRemainingCount(0)
      }
      if (organizers.length > maxOrganizers) {
        const shuffled = [...organizers].sort(() => 0.5 - Math.random())
        const nStaff = shuffled.slice(0, maxOrganizers)
        setStaff(nStaff)
      } else {
        setStaff(organizers)
      }

      //calculation of members
      const membersNotOrganizers =
        participants &&
        participants.length > 0 &&
        participants.filter((participant) => participant.role !== 'organizer' && participant.role !== 'co-organizer')
      if (membersNotOrganizers && membersNotOrganizers.length > 0) {
        const organizerCount = organizers.length > maxOrganizers ? maxOrganizers : organizers.length
        const membersCount = total - organizerCount
        const shuffledMembers = [...membersNotOrganizers].sort(() => 0.5 - Math.random())
        const nMembers = shuffledMembers.slice(0, membersCount)
        setMembers([...nMembers])
      }
    }
    return () => {
      setRemainingCount(0)
    }
  }, [isMobile && totalUserCount > 0, isTablet && totalUserCount > 0, totalUserCount])

  const processUsersInBulk = (users) => {
    return Promise.all(
      users.map((user) =>
        getDescryptedMedia(user.profile_picture, user.is_original, user.original_tag, user.mime_type, user.id, true)
      )
    )
      .then((data) => {
        const decrypted = users.map((user) => {
          const imgObj = data.find((d) => d.id === user.id)
          user.decryptedProfilePicture = imgObj ? imgObj.img : null
          return user
        })
        return Promise.resolve(decrypted)
      })
      .catch((error) => {
        return Promise.reject([])
      })
  }

  const decryptProfilePictures = (type) => {
    if (type === 'member') {
      if (members && members.length > 0) {
        processUsersInBulk(members)
          .then((decryptedUsers) => setMembersWithDecryptedPic(decryptedUsers))
          .catch((error) => setMembersWithDecryptedPic(members))
      }
    } else {
      if (staff && staff.length > 0) {
        processUsersInBulk(staff)
          .then((decryptedUsers) => setStaffWithDecryptedPic(decryptedUsers))
          .catch((error) => setStaffWithDecryptedPic(staff))
      }
    }
  }

  useEffect(() => {
    decryptProfilePictures('member')
  }, [members])

  useEffect(() => {
    decryptProfilePictures('staff')
  }, [staff])

  const hideUserModal = () => {
    setModalData({
      ...modalData,
      isShowUserModal: false
    })
  }

  const showUserModal = (user) => {
    setModalData({
      isShowUserModal: true,
      selectedModalUser: user
    })
  }
  const handleAddCoorganizerRole = (userId) => {
    handleAddCoorganizer(userId)
  }

  const handleRemoveCoorganizerRole = (userId) => {
    handleRemoveCoorganizer(userId)
  }

  const removeFromGroup = (userId) => {
    if (handleRemoveFromGroup) {
      handleRemoveFromGroup(userId)
    }
  }
  const getOptionsBasedOnRole = () => {
    let options = []
    if (loggedInUser.id !== modalData.selectedModalUser.id && !isMixrOver) {
      if (isStaffMember) {
        if (modalData.selectedModalUser.role === 'co-organizer') {
          options.push({
            text: type === 'mixr' ? t('mixrHome.members.removeRole') : t('groupHome.members.removeRole'),
            onClick: () => handleRemoveCoorganizerRole(modalData.selectedModalUser.id)
          })
        } else if (
          modalData.selectedModalUser.role === 'member' ||
          modalData.selectedModalUser.role === 'participant'
        ) {
          options.push({
            text: type === 'mixr' ? t('mixrHome.members.addCoOrganizer') : t('groupHome.members.addCoOrganizer'),
            onClick: () => handleAddCoorganizerRole(modalData.selectedModalUser.id)
          })
        }
        if (type === 'group' && modalData.selectedModalUser.role !== 'none') {
          options.push({
            text: t('groupHome.members.removeMember'),
            onClick: () => removeFromGroup(modalData.selectedModalUser.id)
          })
        }
      }
    }
    return options
  }

  return totalUserCount > 0 ? (
    <div className={`${styles.usersWrapper} ${totalUserCount === 1 && styles.participantsSingle} ${className}`}>
      {show && (
        <React.Fragment>
          <div className={`${styles.organizers} ${totalUserCount === 1 && styles.organizersSingle} ${organizerCSS}`}>
            {staffWithDecryptedPic && staffWithDecryptedPic.length > 0 && (
              <div className={styles.orgText}>
                {t('global.orgAbbr')
                  .split('')
                  .map((ch, idx) => (
                    <span key={idx}>{ch}</span>
                  ))}
              </div>
            )}

            {staffWithDecryptedPic &&
              staffWithDecryptedPic.map((organizer, index) => (
                <Avatar
                  key={organizer.id}
                  imgWrapperSize={'small'}
                  userId={organizer.id}
                  profilePicture={organizer.profile_picture}
                  firstname={organizer.firstname}
                  lastname={organizer.lastname}
                  mimeType={organizer.mime_type}
                  originalTag={organizer.original_tag}
                  isOriginal={organizer.is_original}
                  hasOrangeBorder={true}
                  containerCSS={styles.user}
                  onClickAction={() => showUserModal(organizer)}
                  decryptedProfilePicture={organizer.decryptedProfilePicture}
                  withExpertiseGroup={organizer.with_expertise_group}
                />
              ))}
          </div>
          <div className={styles.members}>
            {membersWithDecryptedPic &&
              membersWithDecryptedPic.map((member, index) => (
                <Avatar
                  key={member.id}
                  imgWrapperSize={'small'}
                  userId={member.id}
                  profilePicture={member.profile_picture}
                  firstname={member.firstname}
                  lastname={member.lastname}
                  mimeType={member.mime_type}
                  originalTag={member.original_tag}
                  isOriginal={member.is_original}
                  containerCSS={styles.user}
                  onClickAction={() => showUserModal(member)}
                  decryptedProfilePicture={member.decryptedProfilePicture}
                  withExpertiseGroup={member.with_expertise_group}
                />
              ))}
          </div>
          {remainingCount > 0 ? (
            <button className={styles.remainingCountWrapper} onClick={selectParticipantsTab}>
              <span>{`+${remainingCount}`}</span>
              <Icon name='arrow-right' />
            </button>
          ) : null}
        </React.Fragment>
      )}
      {modalData.isShowUserModal && (
        <LightProfileModal
          show={modalData.isShowUserModal}
          userIdwithRole={modalData.selectedModalUser.id}
          roleManagementOptions={getOptionsBasedOnRole()}
          closeModalWithRole={hideUserModal}
        />
      )}
    </div>
  ) : null
}

export default MemberOverview
